import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar, Box, Button, Dialog, Typography } from '@mui/material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import VideocamIcon from '@mui/icons-material/Videocam';
import socketClient from '../../socket';
import { handleAlert } from '../../utils/handleAlert';

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

// Global 1:1 call layer: renders the incoming-call and outgoing-call dialogs
// anywhere inside an organization, and exposes startCall() to the chat UI.
export default function CallProvider({ children }) {
  const navigate = useNavigate();
  const { orgName } = useParams();
  const [incoming, setIncoming] = useState(null); // {meetingId, code, caller}
  const [outgoing, setOutgoing] = useState(null); // {name, meetingId, code}
  const outgoingRef = useRef(null);
  outgoingRef.current = outgoing;

  useEffect(() => {
    const onIncoming = (data) => setIncoming(data);
    const onHandled = () => setIncoming(null);
    const onMissed = () => setIncoming(null);
    const onRinging = (data) =>
      setOutgoing((current) => (current ? { ...current, ...data } : current));
    const onAccepted = (data) => {
      setOutgoing(null);
      navigate(`/org/${orgName}/meet/${data.code}`);
    };
    const onDeclined = () => {
      setOutgoing(null);
      handleAlert('Call declined', 'info');
    };
    const onTimeout = () => {
      setOutgoing(null);
      handleAlert('No answer', 'info');
    };
    const onError = (data) => {
      setOutgoing(null);
      handleAlert(data?.message || 'Call failed', 'error');
    };

    socketClient.on('call:incoming', onIncoming);
    socketClient.on('call:handled', onHandled);
    socketClient.on('call:missed', onMissed);
    socketClient.on('call:ringing', onRinging);
    socketClient.on('call:accepted', onAccepted);
    socketClient.on('call:declined', onDeclined);
    socketClient.on('call:timeout', onTimeout);
    socketClient.on('call:error', onError);
    return () => {
      socketClient.off('call:incoming', onIncoming);
      socketClient.off('call:handled', onHandled);
      socketClient.off('call:missed', onMissed);
      socketClient.off('call:ringing', onRinging);
      socketClient.off('call:accepted', onAccepted);
      socketClient.off('call:declined', onDeclined);
      socketClient.off('call:timeout', onTimeout);
      socketClient.off('call:error', onError);
    };
  }, [orgName]);

  const startCall = (calleeId, chatId, calleeName) => {
    if (!calleeId || !chatId) return;
    setOutgoing({ name: calleeName || 'user' });
    socketClient.emit('call:invite', { calleeId, chatId });
  };

  const cancelCall = () => {
    if (outgoingRef.current?.meetingId) {
      socketClient.emit('call:cancel', { meetingId: outgoingRef.current.meetingId });
    }
    setOutgoing(null);
  };

  const acceptCall = () => {
    socketClient.emit('call:accept', { meetingId: incoming.meetingId });
    const code = incoming.code;
    setIncoming(null);
    navigate(`/org/${orgName}/meet/${code}`);
  };

  const declineCall = () => {
    socketClient.emit('call:decline', { meetingId: incoming.meetingId });
    setIncoming(null);
  };

  const dialogSx = {
    '& .MuiDialog-paper': {
      bgcolor: 'var(--color-bg-secondary)',
      color: 'var(--color-text)',
      borderRadius: '18px',
      p: 3,
      minWidth: '300px',
      textAlign: 'center'
    }
  };

  return (
    <CallContext.Provider value={{ startCall }}>
      {children}

      <Dialog open={Boolean(incoming)} sx={dialogSx}>
        <Avatar
          src={incoming?.caller?.avatar}
          sx={{ width: 62, height: 62, mx: 'auto', mb: 1.5, fontSize: '1.6rem' }}
        >
          {incoming?.caller?.name?.charAt(0)}
        </Avatar>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
          {incoming?.caller?.name}
        </Typography>
        <Typography sx={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', mb: 2.5 }}>
          Incoming video call…
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<CallEndIcon />}
            onClick={declineCall}
            sx={{
              bgcolor: 'var(--color-error)',
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: 'var(--color-error-dark)' }
            }}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            startIcon={<VideocamIcon />}
            onClick={acceptCall}
            sx={{
              bgcolor: 'var(--color-success)',
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: 'var(--color-success-dark)' }
            }}
          >
            Accept
          </Button>
        </Box>
      </Dialog>

      <Dialog open={Boolean(outgoing)} sx={dialogSx}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
          Calling {outgoing?.name}…
        </Typography>
        <Typography sx={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', mb: 2.5 }}>
          Waiting for them to answer
        </Typography>
        <Button
          variant="contained"
          startIcon={<CallEndIcon />}
          onClick={cancelCall}
          sx={{
            bgcolor: 'var(--color-error)',
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 600,
            mx: 'auto',
            '&:hover': { bgcolor: 'var(--color-error-dark)' }
          }}
        >
          Cancel
        </Button>
      </Dialog>
    </CallContext.Provider>
  );
}
